import { type Tables } from '@auctisync/shared';
import { AUCTION_ROW_COLUMNS, type AuctionRow } from '../../lib/rowColumns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  PrintableInvoice,
  type AddressBlob,
  type InvoiceBuyer,
  type InvoicePrintContext,
} from '../../components/PrintableInvoice';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

type Invoice = Tables<'invoices'>;
type Line = Tables<'invoice_line_items'>;
type Auction = AuctionRow;

/**
 * /admin/invoices/:id/print AND /my/invoices/:id/print — the branded,
 * browser-printable invoice (Feature 2). One route component serves both
 * surfaces: the get_invoice_print_context RPC gates visibility server-side
 * (invoice owner / tenant staff / super_admin), so the same fetch is safe from
 * either entry point.
 *
 * On load it fetches the invoice + line items + auction (for the field-level
 * pickup fallback) and the tenant's print context, renders <PrintableInvoice>
 * with no app chrome, and auto-opens the print dialog once. A .no-print button
 * lets the user re-open the dialog (and is hidden in the printed output).
 */

/**
 * Typed bridge for get_invoice_print_context. `database.ts` is regenerated
 * centrally after the migration is applied; until then this name isn't in the
 * generated rpc union, so we call through a narrow, fully-typed signature
 * (no `any`) rather than widening the shared client. Remove once db:types runs.
 */
type PrintRpc = (
  fn: 'get_invoice_print_context',
  args: { p_invoice_id: string },
) => Promise<{ data: unknown; error: { message: string } | null }>;
const printRpc = supabase.rpc.bind(supabase) as unknown as PrintRpc;

function toPrintContext(raw: unknown): InvoicePrintContext | null {
  // The RPC returns setof (0/1 row) — supabase-js gives an array for table fns.
  const row: unknown = Array.isArray(raw) ? (raw as unknown[])[0] : raw;
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const obj = (v: unknown): Record<string, unknown> =>
    v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
  return {
    tenant_id: typeof o['tenant_id'] === 'string' ? o['tenant_id'] : '',
    display_name: typeof o['display_name'] === 'string' ? o['display_name'] : 'Auction house',
    branding: obj(o['branding']),
    invoice_settings: obj(o['invoice_settings']),
  };
}

/**
 * Buyer name + address from the same RPC row. The RPC gates this PII to the
 * invoice owner / tenant staff / super_admin, so it's safe to render — the last
 * name drives the pickup band, the full name + address the Bill-to block.
 */
function buyerFromPrintContext(raw: unknown): {
  name: string | null;
  last_name: string | null;
  address: AddressBlob | null;
} {
  const row: unknown = Array.isArray(raw) ? (raw as unknown[])[0] : raw;
  if (!row || typeof row !== 'object') return { name: null, last_name: null, address: null };
  const o = row as Record<string, unknown>;
  const str = (v: unknown): string | undefined =>
    typeof v === 'string' && v.trim() ? v : undefined;
  const first = str(o['buyer_first_name']);
  const last = str(o['buyer_last_name']);
  const address: AddressBlob = {
    line1: str(o['buyer_address_line1']),
    line2: str(o['buyer_address_line2']),
    city: str(o['buyer_city']),
    region: str(o['buyer_region']),
    postal_code: str(o['buyer_postal_code']),
    country: str(o['buyer_country']),
  };
  const hasAddr = Object.values(address).some(Boolean);
  return {
    name: [first, last].filter(Boolean).join(' ') || null,
    last_name: last ?? null,
    address: hasAddr ? address : null,
  };
}

export function InvoicePrint() {
  const { id } = useParams();
  const { session, loading: sessionLoading, userId } = useSession();
  // Strings, so the invoice is read once per visitor, not on every return to
  // the tab (see useSession).
  const email = session?.user.email ?? null;

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [auction, setAuction] = useState<Auction | null>(null);
  const [printContext, setPrintContext] = useState<InvoicePrintContext | null>(null);
  const [buyer, setBuyer] = useState<InvoiceBuyer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const printedRef = useRef(false);

  const load = useCallback(async () => {
    if (!id || userId === null) return;
    setLoading(true);
    setError(null);

    const [{ data: invRow, error: invErr }, { data: lineRows, error: lineErr }, printCtxRes] =
      await Promise.all([
        supabase.from('invoices').select('*').eq('id', id).maybeSingle(),
        supabase
          .from('invoice_line_items')
          .select('*')
          .eq('invoice_id', id)
          .order('sort_order', { ascending: true }),
        printRpc('get_invoice_print_context', { p_invoice_id: id }),
      ]);

    if (invErr || lineErr) {
      setError(invErr?.message ?? lineErr?.message ?? 'Could not load invoice.');
      setLoading(false);
      return;
    }
    if (!invRow) {
      setError('Invoice not found.');
      setLoading(false);
      return;
    }

    const inv = invRow as Invoice;
    setInvoice(inv);
    setLines((lineRows ?? []) as Line[]);
    setPrintContext(printCtxRes.error ? null : toPrintContext(printCtxRes.data));

    // Auction — for the field-level pickup fallback (pickup_* columns).
    if (inv.auction_id) {
      const { data: auctionRow } = await supabase
        .from('auctions')
        .select(AUCTION_ROW_COLUMNS)
        .eq('id', inv.auction_id)
        .maybeSingle();
      setAuction((auctionRow ?? null) as Auction | null);
    }

    // Bill-to + pickup band: buyer name/address come from the print-context RPC
    // (gated to owner/staff/super_admin); the paddle from tenant_buyers; the
    // email only when the caller is viewing their own invoice.
    const isOwnInvoice = inv.user_id === userId;
    const pcBuyer = printCtxRes.error
      ? { name: null, last_name: null, address: null }
      : buyerFromPrintContext(printCtxRes.data);
    const { data: tb } = await supabase
      .from('tenant_buyers')
      .select('tenant_bidder_number')
      .eq('id', inv.tenant_buyer_id)
      .maybeSingle();
    setBuyer({
      name: pcBuyer.name,
      last_name: pcBuyer.last_name,
      address: pcBuyer.address,
      email: isOwnInvoice ? email : null,
      bidder_number:
        (tb as { tenant_bidder_number: number | null } | null)?.tenant_bidder_number ?? null,
    });

    setLoading(false);
  }, [id, userId, email]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-open the print dialog once, after the invoice has rendered.
  useEffect(() => {
    if (loading || error || !invoice || printedRef.current) return;
    printedRef.current = true;
    const t = window.setTimeout(() => window.print(), 300);
    return () => window.clearTimeout(t);
  }, [loading, error, invoice]);

  if (sessionLoading) {
    return <main style={statusStyle}>Loading invoice…</main>;
  }
  if (!session) {
    return <main style={statusStyle}>Please sign in to view this invoice.</main>;
  }
  if (loading) {
    return <main style={statusStyle}>Loading invoice…</main>;
  }
  if (error || !invoice) {
    return (
      <main style={statusStyle}>
        <p style={{ color: 'var(--text-danger, #991b1b)' }}>{error ?? 'Invoice not found.'}</p>
        <Link to="/my/invoices" style={{ color: 'var(--brand-primary)' }}>
          ← Back to invoices
        </Link>
      </main>
    );
  }

  return (
    <>
      <div className="no-print" style={toolbarStyle}>
        <button type="button" onClick={() => window.print()} style={printButtonStyle}>
          Print / Save as PDF
        </button>
      </div>
      <PrintableInvoice
        invoice={invoice}
        lines={lines}
        auction={auction}
        printContext={printContext}
        buyer={buyer}
      />
    </>
  );
}

const statusStyle: React.CSSProperties = {
  padding: '2rem 1.25rem',
  maxWidth: '52rem',
  margin: '0 auto',
  color: 'var(--text-muted)',
  display: 'grid',
  gap: '0.75rem',
};

// Floats over the branded desk the component renders; hidden in print (.no-print).
const toolbarStyle: React.CSSProperties = {
  position: 'fixed',
  top: '16px',
  right: '24px',
  zIndex: 10,
};

const printButtonStyle: React.CSSProperties = {
  padding: '0.55rem 1.1rem',
  borderRadius: 'var(--r-control, 0.375rem)',
  border: '1px solid var(--brand-primary, #0a0045)',
  background: 'var(--brand-primary, #0a0045)',
  color: '#ffffff',
  fontSize: '0.9rem',
  fontWeight: 600,
  cursor: 'pointer',
};
